/**
 * DensityHeatmap — Leaflet polygon overlay driven by live DensitySnapshot data.
 *
 * Renders one colour-coded L.polygon per zone inside a react-leaflet MapContainer.
 * Colour scale mirrors the zone status thresholds:
 *   0–69%  → #3F9D6E  (normal / green)
 *   70–84% → #D97706  (warning / amber)
 *   85%+   → #C2410C  (alert / ember)
 *
 * Zones with no snapshot yet are rendered in neutral grey so the geometry is
 * always visible even before the first tick arrives.
 *
 * No PII — occupancyCount is an anonymous aggregate integer count.
 */

import { Polygon } from 'react-leaflet';
import type { LatLngTuple } from 'leaflet';
import type { DensitySnapshot, Zone } from '@vigilo/shared';

export interface DensityHeatmapProps {
  zones:     Zone[];
  snapshots: Record<string, DensitySnapshot>;
  /** Fill opacity 0–1; controlled by the dashboard opacity slider. */
  opacity:   number;
}

function fillColor(utilisation: number): string {
  if (utilisation >= 85) return '#C2410C';  // ember  — alert
  if (utilisation >= 70) return '#D97706';  // amber  — warning
  return '#3F9D6E';                         // green  — normal
}

// GeoJSON stores coordinates as [longitude, latitude].
// Leaflet expects [latitude, longitude]. Flip each pair.
function toLeafletRings(coordinates: number[][][]): LatLngTuple[][] {
  return coordinates.map(ring =>
    ring.map(([lng, lat]) => [lat, lng] as LatLngTuple)
  );
}

export function DensityHeatmap({ zones, snapshots, opacity }: DensityHeatmapProps) {
  return (
    <>
      {zones.map(zone => {
        if (zone.geometry.type !== 'Polygon') return null;

        const snap  = snapshots[zone.id];
        const util  = snap?.capacityUtilisation ?? 0;
        const color = snap ? fillColor(util) : '#6B7280';  // grey until first tick
        const rings = toLeafletRings(zone.geometry.coordinates as number[][][]);

        return (
          <Polygon
            key={zone.id}
            positions={rings}
            pathOptions={{
              fillColor:   color,
              fillOpacity: opacity,
              weight:      0,
              interactive: false,
            }}
          />
        );
      })}
    </>
  );
}
